import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { departmentService } from '../../services/departmentService';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { 
  SECTORS,
  USER_ROLES,
  isNonDepartmentRole,
  USER_STATUS,
  USER_STATUS_OPTIONS,
  ROLE_LABELS,
  getSectorLabel
} from '../../constants/authConstants';
import { dateUtils } from '../../utils/helpers';

const EditUserModal = ({ isOpen, onClose, user, onUpdate }) => {
  const { isMobile, getResponsiveValue } = useTheme();
  
  const [formData, setFormData] = useState({
    role: USER_ROLES.DEPARTMENT,
    sector: '',
    department: '',
    status: USER_STATUS.ACTIVE
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const gridCols = getResponsiveValue({
    mobile: 'grid-cols-1',
    desktop: 'grid-cols-2'
  });

  const gridGap = getResponsiveValue({
    mobile: 'gap-3',
    desktop: 'gap-4'
  });

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const buttonSpacing = getResponsiveValue({
    mobile: 'flex-col space-y-3',
    desktop: 'flex-row space-x-3'
  });

  // Initialize form data when modal opens with user data
  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        role: user.role || USER_ROLES.DEPARTMENT,
        sector: user.sector || '',
        department: user.department || '',
        status: user.status || USER_STATUS.ACTIVE
      });
      loadDepartments();
    }
  }, [isOpen, user]);

  // Filter departments based on selected sector
  useEffect(() => {
    if (formData.sector) {
      const depts = departments.filter(d => 
        d.sector === formData.sector && d.isActive
      );
      setFilteredDepartments(depts);
    } else {
      setFilteredDepartments([]);
    }
  }, [formData.sector, departments]);

  const loadDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const allDepartments = await departmentService.getActiveDepartments();
      setDepartments(allDepartments);
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear department when sector changes
    if (field === 'sector') {
      setFormData(prev => ({
        ...prev,
        department: ''
      }));
    }

    // Clear sector/department for Admin and Planning Official roles
    if (field === 'role' && isNonDepartmentRole(value)) {
      setFormData(prev => ({
        ...prev,
        sector: '',
        department: ''
      }));
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sector;
        delete newErrors.department;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only validate sector/department for Department users
    if (formData.role === USER_ROLES.DEPARTMENT) {
      if (!formData.sector) {
        newErrors.sector = 'Sector is required for department users';
      }
      
      if (!formData.department) {
        newErrors.department = 'Department is required for department users';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const updateData = {
        role: formData.role,
        status: formData.status
      };

      // Only add sector/department for Department users
      if (formData.role === USER_ROLES.DEPARTMENT) {
        updateData.sector = formData.sector;
        updateData.department = formData.department;
      } else {
        // Clear sector/department for non-department roles
        updateData.sector = null;
        updateData.department = null;
      }
      
      await onUpdate(user, updateData);
      
      handleClose();
    } catch (error) {
      console.error('Error updating user:', error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        role: USER_ROLES.DEPARTMENT,
        sector: '',
        department: '',
        status: USER_STATUS.ACTIVE
      });
      setErrors({});
      onClose();
    }
  };

  const getDepartmentOptions = () => {
    if (isNonDepartmentRole(formData.role)) {
      return [{ value: '', label: 'Not applicable for this role' }];
    }

    if (!formData.sector) {
      return [{ value: '', label: 'Select a sector first' }];
    }
    
    if (loadingDepartments) {
      return [{ value: '', label: 'Loading departments...' }];
    }
    
    if (filteredDepartments.length === 0) {
      return [{ value: '', label: 'No departments available' }];
    }
    
    return filteredDepartments
      .sort((a, b) => (a.priority || 999) - (b.priority || 999))
      .map(dept => ({
        value: dept.departmentCode,
        label: dept.departmentName
      }));
  };

  const isNonDepartmentUser = isNonDepartmentRole(formData.role);

  // Get the department name for display
  const getCurrentDepartmentName = () => {
    if (!user?.department || !departments.length) return user?.department;
    const dept = departments.find(d => d.departmentCode === user.department);
    return dept ? dept.departmentName : user.department;
  };

  // Check if any changes were made
  const hasChanges = () => {
    if (!user) return false;
    return (
      formData.role !== user.role ||
      formData.status !== user.status ||
      formData.sector !== (user.sector || '') ||
      formData.department !== (user.department || '')
    );
  };

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Edit User - ${user.fullName}`}
      size="lg"
      maxHeight="large"
      closeOnOverlay={!loading}
    >
      <div className={containerPadding}>
        {errors.general && (
          <div className="mb-4 bg-error-subtle border border-border-error rounded-xl p-3">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-error">{errors.general}</span>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* User Information Display */}
          <div className="bg-surface-secondary rounded-xl p-4">
            <h4 className="text-sm font-semibold text-text-primary mb-3">User Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className="text-text-tertiary w-28">Name:</span>
                <span className="text-text-primary font-medium">{user.fullName}</span>
              </div>
              <div className="flex items-center">
                <span className="text-text-tertiary w-28">Email:</span>
                <span className="text-text-primary">{user.email}</span>
              </div>
              <div className="flex items-center">
                <span className="text-text-tertiary w-28">Registered:</span>
                <span className="text-text-primary">{dateUtils.formatDate(user.createdAt)}</span>
              </div>
              {user.lastLoginAt && (
                <div className="flex items-center">
                  <span className="text-text-tertiary w-28">Last Login:</span>
                  <span className="text-text-primary">{dateUtils.formatDate(user.lastLoginAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Current Assignment Display */}
          <div className="bg-surface-secondary border border-border-primary rounded-xl p-4">
            <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-text-tertiary" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Current Assignment
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className="text-text-tertiary w-28">Role:</span>
                <span className="text-text-primary">{ROLE_LABELS[user.role] || user.role}</span>
              </div>
              {user.sector && (
                <div className="flex items-center">
                  <span className="text-text-tertiary w-28">Sector:</span>
                  <span className="text-text-primary">{getSectorLabel(user.sector)}</span>
                </div>
              )}
              {user.department && (
                <div className="flex items-center">
                  <span className="text-text-tertiary w-28">Department:</span>
                  <span className="text-text-primary">{getCurrentDepartmentName()}</span>
                </div>
              )}
              <div className="flex items-center">
                <span className="text-text-tertiary w-28">Status:</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  user.status === USER_STATUS.ACTIVE ? 'bg-success-subtle text-success' :
                  user.status === USER_STATUS.INACTIVE ? 'bg-gray-100 text-gray-800' :
                  user.status === USER_STATUS.SUSPENDED ? 'bg-orange-100 text-orange-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {user.status}
                </span>
              </div>
            </div>
          </div>

          {/* Edit Form */}
          <div className="space-y-4">
            <div className="border-t border-border-secondary pt-4">
              <h4 className="text-sm font-semibold text-text-primary mb-3">Update Role & Access</h4>
            </div>

            <div className={`grid ${gridCols} ${gridGap}`}>
              <Select
                label="User Role"
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                options={[
                  { value: USER_ROLES.DEPARTMENT, label: ROLE_LABELS[USER_ROLES.DEPARTMENT] },
                  { value: USER_ROLES.PLANNING_OFFICIAL, label: ROLE_LABELS[USER_ROLES.PLANNING_OFFICIAL] },
                  { value: USER_ROLES.DONER, label: ROLE_LABELS[USER_ROLES.DONER] },
                  { value: USER_ROLES.ADMIN, label: ROLE_LABELS[USER_ROLES.ADMIN] }
                ]}
                required
                disabled={loading}
                className="text-sm"
              />

              <Select
                label="Account Status"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                options={USER_STATUS_OPTIONS.filter(s => s.value !== USER_STATUS.PENDING).map(status => ({
                  value: status.value,
                  label: status.label
                }))}
                required
                disabled={loading}
                className="text-sm"
              />
            </div>

            {isNonDepartmentUser && (
              <div className="bg-info-subtle border border-border-primary rounded-xl p-3">
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-info mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-sm text-info">
                    <p className="font-medium mb-1">
                      {ROLE_LABELS[formData.role]} Access
                    </p>
                    <p className="text-xs">
                      {formData.role === USER_ROLES.ADMIN 
                        ? 'Full system access with user management capabilities.' 
                        : 'View-only user management with full planning access.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {formData.role === USER_ROLES.DEPARTMENT && (
              <div className={`grid ${gridCols} ${gridGap}`}>
                <Select
                  label="Sector"
                  value={formData.sector}
                  onChange={(e) => handleInputChange('sector', e.target.value)}
                  options={[
                    { value: '', label: 'Select a sector' },
                    ...SECTORS
                  ]}
                  error={errors.sector}
                  required
                  disabled={loading}
                  className="text-sm"
                />

                <Select
                  label="Department"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  options={getDepartmentOptions()}
                  error={errors.department}
                  required
                  disabled={!formData.sector || loading || loadingDepartments}
                  className="text-sm"
                />
              </div>
            )}
          </div>

          {/* Warning if changing from current role */}
          {formData.role !== user.role && (
            <div className="bg-warning-subtle border border-border-primary rounded-xl p-3">
              <div className="flex items-start">
                <svg className="w-4 h-4 text-warning mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-warning">
                  <p className="font-medium mb-1">Role Change Detected</p>
                  <p className="text-xs">
                    Changing from <strong>{ROLE_LABELS[user.role]}</strong> to <strong>{ROLE_LABELS[formData.role]}</strong>. 
                    This will update the user's access permissions immediately.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className={`flex ${buttonSpacing} pt-4 border-t border-border-primary`}>
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              disabled={loading}
              className={getResponsiveValue({ mobile: 'w-full', desktop: 'flex-1' })}
            >
              Cancel
            </Button>
            
            <Button
              type="button"
              onClick={handleSave}
              loading={loading}
              disabled={loading || loadingDepartments || !hasChanges()}
              className={getResponsiveValue({ mobile: 'w-full', desktop: 'flex-1' })}
            >
              {loading ? 'Saving Changes...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditUserModal;